import * as i0 from '@angular/core';
import { EventEmitter, Input, Output, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/platform-browser';

/**
 * An empty state component that can be used to display an empty state message.
 * @example
 * <e11-empty-state title="No data found" subtitle="Please try again later"></e11-empty-state>
 * @example
 * <e11-empty-state title="No data found" subtitle="Please try again later" icon="error"></e11-empty-state>
 * @example
 * <e11-empty-state title="No data found" subtitle="Please try again later" icon="error" iconType="outlined"></e11-empty-state>
 * @example
 * <e11-empty-state title="No data found" subtitle="Please try again later" icon="error" iconType="outlined" iconSize="md-36"></e11-empty-state>
 */
class E11EmptyStateComponent {
    /**
     * Sets the subtitle HTML content, bypassing security to allow for safe HTML.
     * @param {string} htmlContent - The HTML content to set as subtitle.
     */
    set htmlSubtitle(htmlContent) {
        this._safeHtmlSubtitle = this.domSanitizer.bypassSecurityTrustHtml(htmlContent);
    }
    constructor(domSanitizer) {
        this.domSanitizer = domSanitizer;
        /**
         * The type of the icon. Can be 'outlined', 'two-tone', 'sharp', or empty for the default 'filled'.
         * @type {IconType|undefined}
         * @default ''
         */
        this.iconType = '';
        /**
         * The size of the icon. Can be 'md-24', 'md-18', 'md-36', or 'md-48'. Defaults to 'md-24'.
         * @type {IconSize|undefined}
         * @default 'md-24'
         */
        this.iconSize = 'md-24';
        /**
         * Indicates if the button should be displayed.
         * @type {boolean|undefined}
         * @default false
         */
        this.showButton = false;
        /**
         * The border color for the button.
         * @type {string|undefined}
         * @default 'e11-border-skin-grey/50'
         */
        this.borderColor = 'e11-border-skin-grey/50';
        /**
         * The value of the button.
         * @type {string|undefined}
         */
        this.buttonValue = '';
        /**
         * The color of the button.
         * @type {string|undefined}
         */
        this.buttonColor = 'e11-border-skin-primary-accent';
        /**
         * Whether the button is disabled.
         * @type {boolean}
         */
        this.buttonDisabled = false;
        /**
         * The event emitted when the button is clicked.
         * @type {EventEmitter<void>}
         */
        this.buttonClicked = new EventEmitter();
        /**
         * The border style of the component. Defaults to 'dashed'.
         * @type {Border}
         * @default 'dashed'
         */
        this.border = 'dashed';
        /**
         * The title text of the component. Defaults to 'Empty'.
         * @type {string|undefined}
         * @default 'Empty'
         */
        this.title = 'Empty';
        /**
         * Indicates if the component is clickable. Defaults to true.
         * @type {boolean|undefined}
         * @default true
         */
        this.clickable = true;
        /**
         * Indicates if the component is disabled. Defaults to false.
         * @type {boolean|undefined}
         * @default false
         */
        this.disabled = false;
        /**
         * The text to display when the component is disabled. Defaults to 'disabledText'.
         * @type {string|undefined}
         * @default 'disabledText'
         */
        this.disabledText = 'disabledText';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11EmptyStateComponent, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11EmptyStateComponent, isStandalone: true, selector: "e11-empty-state", inputs: { icon: "icon", iconType: "iconType", iconSize: "iconSize", customImageUrl: "customImageUrl", customImageAltText: "customImageAltText", showButton: "showButton", borderColor: "borderColor", buttonValue: "buttonValue", buttonColor: "buttonColor", buttonDisabled: "buttonDisabled", buttonIcon: "buttonIcon", border: "border", title: "title", subtitle: "subtitle", clickable: "clickable", disabled: "disabled", disabledText: "disabledText", containerClass: "containerClass", contentClass: "contentClass", containerClassOverrides: "containerClassOverrides", contentClassOverrides: "contentClassOverrides", htmlSubtitle: "htmlSubtitle" }, outputs: { buttonClicked: "buttonClicked" }, ngImport: i0, template: ` <div
    class="e11-relative e11-block e11-w-full e11-rounded-md e11-p-6 e11-text-center {{ borderColor }} {{ containerClassOverrides }} {{
      containerClass
    }}"
    [ngClass]="{
      'e11-cursor-pointer hover:e11-border-skin-primary-accent hover:e11-bg-skin-primary-accent/10 focus:e11-outline-none focus:e11-ring-2 focus:e11-ring-offset-2 focus:e11-ring-secondary':
        clickable,
      'e11-cursor-not-allowed e11-opacity-70': disabled && clickable,
      'e11-cursor-default': !clickable,
      'e11-border-2 e11-border-dashed': border === 'dashed',
      'e11-border': border === 'solid',
      'e11-border-0': !border
    }"
    [title]="disabled ? disabledText : ''"
  >
    <img
      *ngIf="customImageUrl && !icon"
      class="e11-image e11-w-9 e11-h-9 e11-mx-auto e11-mb-1"
      src="{{ customImageUrl }}"
      alt="{{ customImageAltText }}"
    />
    <span *ngIf="icon" [ngClass]="iconType ? 'material-icons-' + iconType : 'material-icons'" class="{{ iconSize }} e11-mx-auto"> {{ icon }} </span>
    <h4 class="e11-font-bold">{{ title }}</h4>
    <span *ngIf="subtitle">
      <p class="e11-text-sm">{{ subtitle }}</p>
    </span>
    <div *ngIf="_safeHtmlSubtitle" class="e11-text-sm e11-text-skin-light" [innerHTML]="_safeHtmlSubtitle"></div>
    <button
      *ngIf="showButton"
      class="e11-bg-transparent e11-px-6 e11-py-2 e11-rounded-full e11-inline-flex e11-items-center e11-justify-center e11-truncate e11-border {{
        buttonColor
      }}"
      (click)="!disabled && buttonClicked.emit()"
    >
      <span class="material-icons-outlined e11-mr-2">
        {{ buttonIcon }}
      </span>
      {{ buttonValue }}
    </button>
  </div>`, isInline: true, styles: [""], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11EmptyStateComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-empty-state', standalone: true, imports: [CommonModule], encapsulation: ViewEncapsulation.None, template: ` <div
    class="e11-relative e11-block e11-w-full e11-rounded-md e11-p-6 e11-text-center {{ borderColor }} {{ containerClassOverrides }} {{
      containerClass
    }}"
    [ngClass]="{
      'e11-cursor-pointer hover:e11-border-skin-primary-accent hover:e11-bg-skin-primary-accent/10 focus:e11-outline-none focus:e11-ring-2 focus:e11-ring-offset-2 focus:e11-ring-secondary':
        clickable,
      'e11-cursor-not-allowed e11-opacity-70': disabled && clickable,
      'e11-cursor-default': !clickable,
      'e11-border-2 e11-border-dashed': border === 'dashed',
      'e11-border': border === 'solid',
      'e11-border-0': !border
    }"
    [title]="disabled ? disabledText : ''"
  >
    <img
      *ngIf="customImageUrl && !icon"
      class="e11-image e11-w-9 e11-h-9 e11-mx-auto e11-mb-1"
      src="{{ customImageUrl }}"
      alt="{{ customImageAltText }}"
    />
    <span *ngIf="icon" [ngClass]="iconType ? 'material-icons-' + iconType : 'material-icons'" class="{{ iconSize }} e11-mx-auto"> {{ icon }} </span>
    <h4 class="e11-font-bold">{{ title }}</h4>
    <span *ngIf="subtitle">
      <p class="e11-text-sm">{{ subtitle }}</p>
    </span>
    <div *ngIf="_safeHtmlSubtitle" class="e11-text-sm e11-text-skin-light" [innerHTML]="_safeHtmlSubtitle"></div>
    <button
      *ngIf="showButton"
      class="e11-bg-transparent e11-px-6 e11-py-2 e11-rounded-full e11-inline-flex e11-items-center e11-justify-center e11-truncate e11-border {{
        buttonColor
      }}"
      (click)="!disabled && buttonClicked.emit()"
    >
      <span class="material-icons-outlined e11-mr-2">
        {{ buttonIcon }}
      </span>
      {{ buttonValue }}
    </button>
  </div>` }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }], propDecorators: { icon: [{
                type: Input
            }], iconType: [{
                type: Input
            }], iconSize: [{
                type: Input
            }], customImageUrl: [{
                type: Input
            }], customImageAltText: [{
                type: Input
            }], showButton: [{
                type: Input
            }], borderColor: [{
                type: Input
            }], buttonValue: [{
                type: Input
            }], buttonColor: [{
                type: Input
            }], buttonDisabled: [{
                type: Input
            }], buttonIcon: [{
                type: Input
            }], buttonClicked: [{
                type: Output
            }], border: [{
                type: Input
            }], title: [{
                type: Input
            }], subtitle: [{
                type: Input
            }], clickable: [{
                type: Input
            }], disabled: [{
                type: Input
            }], disabledText: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], contentClass: [{
                type: Input
            }], containerClassOverrides: [{
                type: Input
            }], contentClassOverrides: [{
                type: Input
            }], htmlSubtitle: [{
                type: Input
            }] } });

class E11EmptyStateModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11EmptyStateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11EmptyStateModule, imports: [E11EmptyStateComponent], exports: [E11EmptyStateComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11EmptyStateModule, imports: [E11EmptyStateComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11EmptyStateModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11EmptyStateComponent],
                    exports: [E11EmptyStateComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11EmptyStateComponent, E11EmptyStateModule };
